import { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const ZonesContext = createContext(null);

export function ZonesProvider({ children }) {
  const [zones, setZones] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/zones");
      setZones(data);
    } catch (err) {
      console.error("Failed to load zones:", err);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createZone = useCallback(async ({ name, kind, shelves_count }) => {
    const { data } = await api.post("/zones", { name, kind, shelves_count });
    await refresh();
    return data;
  }, [refresh]);

  const updateZone = useCallback(async (id, patch) => {
    const { data } = await api.put(`/zones/${id}`, patch);
    await refresh();
    return data;
  }, [refresh]);

  const addShelves = useCallback(async (id, count) => {
    const { data } = await api.post(`/zones/${id}/add-shelves`, null, { params: { count } });
    await refresh();
    return data;
  }, [refresh]);

  const deleteZone = useCallback(async (id) => {
    await api.delete(`/zones/${id}`);
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ zones, loaded, refresh, createZone, updateZone, addShelves, deleteZone }),
    [zones, loaded, refresh, createZone, updateZone, addShelves, deleteZone],
  );

  return <ZonesContext.Provider value={value}>{children}</ZonesContext.Provider>;
}

export function useZones() {
  return useContext(ZonesContext);
}
